import type { BlocoLocal } from '../types/editor';

import { NOME_DE_VARIAVEL_VALIDO, variavelDoCampo } from './prazoVigiado';

/**
 * Vínculo entre um "Criar pendência" e a espera que aguarda o desfecho DELA (core#2249).
 *
 * O Criar pendência grava o id da pendência numa variável (`savePendenciaIdToVariable`) e a espera
 * lê essa variável (`pendenciaId: '{{variavel}}'`). No core o vínculo vale só para os desfechos de
 * pendência: resolvida ou expirada de OUTRA pendência não retoma a espera, e os demais eventos da
 * lista (ex.: manifestação) retomam como antes. Mesmo molde do prazo vigiado (`prazoVigiado.ts`).
 */
export const EVENTO_PENDENCIA_RESOLVIDA = 'arbitral.pendencia_resolvida.v1';
export const EVENTO_PENDENCIA_EXPIRADA = 'arbitral.pendencia_expirada.v1';

const EVENTOS_DE_PENDENCIA: readonly string[] = [
  EVENTO_PENDENCIA_RESOLVIDA,
  EVENTO_PENDENCIA_EXPIRADA,
];

type Parametros = BlocoLocal['parametros'];

/**
 * A espera ouve algum desfecho de pendência, e é só aí que o core lê o `pendenciaId`.
 *
 * @example vigiaPendencia({ eventos: [EVENTO_PENDENCIA_EXPIRADA] }) // true
 */
export function vigiaPendencia(p: Parametros): boolean {
  return (
    Array.isArray(p.eventos) && p.eventos.some((evento) => EVENTOS_DE_PENDENCIA.includes(evento))
  );
}

/**
 * O valor do campo "Pendência vigiada", normalizado. Sem desfecho de pendência na lista o core
 * ignora o campo, então ele não vale. Pode ser um literal que não é nome de variável (ex.: id
 * colado direto no `node.data`, D-04): quem decide se vira `{{ }}` é `pendenciaIdDoNode`.
 *
 * @example pendenciaVigiadaDe({ eventos: [EVENTO_PENDENCIA_RESOLVIDA], pendenciaId: '{{ pend_a }}' }) // 'pend_a'
 */
export function pendenciaVigiadaDe(p: Parametros): string | undefined {
  return vigiaPendencia(p) ? variavelDoCampo(p.pendenciaId) : undefined;
}

/**
 * O `pendenciaId` que vai para o `node.data`. Nome de variável vira template (`{{nome}}`); o que não
 * é nome de variável vai como está (D-04 do core#2249): embrulhar um id literal em `{{ }}` faria o
 * template não resolver e o nó falhar ao abrir, em silêncio, no próximo save. O modal aponta o
 * literal como nome inválido, sem travar a publicação.
 *
 * @example pendenciaIdDoNode('pend_a') // '{{pend_a}}'
 * @example pendenciaIdDoNode('3f0c-uuid') // '3f0c-uuid'
 */
export function pendenciaIdDoNode(valor: string): string {
  return NOME_DE_VARIAVEL_VALIDO.test(valor) ? `{{${valor}}}` : valor;
}

/**
 * A variável em que o Criar pendência grava o id. A tela só mostra o campo com "Bloquear avanço"
 * desligado e o limpa ao religar (com bloqueio, a própria pendência pausa o fluxo e ninguém espera
 * por ela em paralelo). Mas o executor grava a variável com ou sem bloqueio
 * (`criarPendenciaExecutor`), então o valor que o bloco já traz vale e é preservado (D-01).
 *
 * @example variavelGravadaPelaPendencia({ bloqueiaAvanco: false, savePendenciaIdToVariable: 'pend_a' }) // 'pend_a'
 */
export function variavelGravadaPelaPendencia(p: Parametros): string | undefined {
  return variavelDoCampo(p.savePendenciaIdToVariable);
}
